import { Grid, GridColumn as Column, GridColumnMenuCheckboxFilter, GridCellProps, GridItemChangeEvent, GridToolbar, GridColumnResizeEvent, GridDataStateChangeEvent, GridColumnMenuFilter } from '@progress/kendo-react-grid';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { MyCommandCell } from '../components/synoptic/MyCommandCell';
import { AccessToken } from '../config/AccessToken';
import { Button } from '@progress/kendo-react-buttons';
import { useNavigate } from 'react-router-dom';

import { process as kendoProcess } from "@progress/kendo-data-query";
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import { GridPDFExport } from '@progress/kendo-react-pdf';
import { fileCsvIcon, fileExcelIcon, filePdfIcon, filterIcon, saveIcon, uploadIcon, warningTriangleIcon } from '@progress/kendo-svg-icons';
import { CSVLink } from 'react-csv';
import { json2csv } from 'json-2-csv';
import { loadingPanel } from '../components/LoadingPanel';
import { ConfiguratorToggleColumnsGrid } from '../components/ConfiguratorToggleColumnsGrid';
import { SvgIcon } from '@progress/kendo-react-common';
import { Switch } from '@progress/kendo-react-inputs';
import { alarm } from '../assets/alarm';
import { EditDescriptor, HeaderThElement } from '@progress/kendo-react-data-tools';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { DatePicker, TimePicker } from '@progress/kendo-react-dateinputs';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

/**
 * SynopticDevices component manages and displays a grid of device information.
 * It includes functionalities for device list retrieval, filtering, sorting,
 * column management, and exporting data to Excel, PDF, and CSV formats.
 *
 * The component interacts with a server to fetch device data, allows editing
 * device names, and provides a UI for toggling column visibility. It also 
 * supports pagination and responsive design adjustments based on grid width.
 *
 * State management is used extensively to manage devices, columns, data states,
 * and UI states such as loading and error handling.
 *
 * Utilizes KendoReact components for grid and toolbar functionalities, and
 * React's useEffect hook to initialize device data on component mount.
 */

const editField = "inEdit";

const EditingCell = (props: any) => {
  const { edit, update, cancel, editField } = props;
  return (
    <MyCommandCell
      {...props}
      edit={edit}
      update={update}
      cancel={cancel}
      editField={editField}
    />
  );
};

// Custom cell for name editing that preserves cursor position
const NameEditCell = (props: GridCellProps) => {
  const { dataItem, field } = props;
  const [localValue, setLocalValue] = useState(dataItem[field || '']);
  const isInEdit = dataItem[editField];

  // Update local value when dataItem changes (e.g., when entering edit mode)
  useEffect(() => {
    if (isInEdit) {
      setLocalValue(dataItem[field || '']);
    }
  }, [isInEdit, dataItem, field]);

  if (!isInEdit) {
    return <td>{dataItem[field || '']}</td>;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    // Update the dataItem only after a small delay to prevent continuous re-renders
    dataItem[field || ''] = e.target.value;
  };

  return (
    <td>
      <input
        className="k-textbox"
        type="text"
        value={localValue || ''}
        onChange={handleChange}
        style={{ width: '100%' }}
        autoFocus
      />
    </td>
  );
};

const LinkMapCell = (props: any) => {

  const navigate = useNavigate()

  const localizationService = useLocalization();
  let lastPositionDate = props?.dataItem?.lastPositionDate;
  let deviceMapId = props?.dataItem?.mapId;
  //let deviceGroupId = props?.dataItem?.groupId;
  let deviceMac = props?.dataItem?.mac;

  const showMap = () => {

    if (deviceMapId != null) {
      localStorage.setItem("deviceMapIdFromSynoptic", deviceMapId);
    }

    if (deviceMac != null) {
      localStorage.setItem("deviceMacFromSynoptic", deviceMac);
    }

    localStorage.removeItem("beaconId")
    localStorage.removeItem("mapIdByBeacon")
    localStorage.removeItem("beaconFromGrid")

    navigate("/home/map")
  }

  return (
    <td className="k-command-cell" style={{ textAlign: 'center' }}>
      <button
        title={
          deviceMapId != null && (lastPositionDate != 0 && lastPositionDate != '-')
            ? localizationService.toLanguageString('synoptic_devices.map', 'NOT_FOUND')
            : localizationService.toLanguageString('synoptic_devices.no_position_found', 'NOT_FOUND')
        }
        className="k-icon k-font-icon k-i-globe synoptic-globe"
        style={{
          color: '#198792',
          margin: '8px',
          border: 'none',
          background: 'none',
          outline: 'none',
          cursor: (deviceMapId != null && lastPositionDate != 0 && lastPositionDate != '-') ? 'pointer' : 'not-allowed',
          opacity: (deviceMapId != null && lastPositionDate != 0 && lastPositionDate != '-') ? 1 : 0.4,
        }}
        disabled={deviceMapId == null && (lastPositionDate == 0 && lastPositionDate == '-')}
        onClick={showMap}
      />
    </td>

  );
};

const LinkDeviceManagement = (props: any) => {

  const localizationService = useLocalization();
  const navigate = useNavigate()
  let deviceMac: any = props?.dataItem?.mac;

  return (
    <td className="k-command-cell" style={{ textAlign: 'center' }}>
      <button
        id='deviceManagement'
        className="k-icon k-font-icon k-i-gears"
        style={{ color: '#198792', margin: '8px', border: 'none', background: 'none', outline: 'none' }}
        onClick={() => navigate("/home/configuration/devices/devices-management", { state: { deviceMac: deviceMac } })}
      >
        <a
          id='deviceManagement'
          title={localizationService.toLanguageString('configuration.devices.devices_management', 'NOT_FOUND')}
        />
      </button>
    </td>
  );
};

export const SynopticDevices = () => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const [edit, setEdit] = useState<EditDescriptor>({});

  const [columns, setColumns] = useState<any>([]);

  const [devices, setDevices] = useState<any>([]);
  const [devicesOn, setDevicesOn] = useState<any>([]);
  const [countDevicesOn, setCountDevicesOn] = useState(0);

  const [error, setError] = useState('');

  const [loading, setLoading] = useState(false);

  const localizationService = useLocalization();
  const [instance, accessTokenRequest] = AccessToken();

  const userEmail = localStorage.getItem('email');
  const statusDevice: any = localStorage.getItem('statusDevice');

  const [pageSizeValue, setPageSizeValue] = useState()

  const [liveStatus, setLiveStatus] = useState<any>(false);
  const [onlyBound, setOnlyBound] = useState<any>(false);

  const [firstLoad, setFirstLoad] = useState(true);
  const [visibleCount, setVisibleCount] = useState(0);

  const [filterName, setFilterName] = useState<any>(() => {
    const filterNameSynoptic = localStorage.getItem('filterNameSynoptic');
    return filterNameSynoptic ? JSON.parse(filterNameSynoptic) : [];
  });

  const [editMode, setEditMode] = useState<any>(false);

  const refreshAPI: any = localStorage.getItem('refreshAPI') ?? 30;

  let languageId: any = localStorage.getItem('languageId');

  const visibleCountSynoptic = JSON.parse(localStorage.getItem("visibleCountSynoptic") || "0");

  useEffect(() => {

    const liveStatus: any = localStorage.getItem('liveStatus');

    // Set a timeout to call getDeviceList with delay based on refreshAPI
    const intervalId = setInterval(() => {
      const filteredColumn: any = localStorage.getItem('filterOpenedColumn');
      if (refreshAPI != null && !editMode && filteredColumn != 'openedColumn') {
        if (liveStatus == 'true') {
          onToggleLive(true)
        } else {
          onToggleLive(false)
        }
      }
    }, refreshAPI * 1000); // Delay in milliseconds

    return () => clearInterval(intervalId);
  }, [devicesOn, editMode]);


  const formatDateFromIsoToString = (date: any) => {

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    if (languageId == 'en-US') {
      hours = hours % 12;
      hours = hours === 0 ? 12 : hours; // convert 0 → 12
      const hours12 = String(hours).padStart(2, '0');
      return `${month}/${day}/${year} ${hours12}:${minutes}:${seconds}`;
    } else {
      const hours24 = String(hours).padStart(2, '0');
      return `${day}/${month}/${year} ${hours24}:${minutes}:${seconds}`;
    }
  }

  const convertDateTime = (dateTime: any) => {

    if (dateTime == null) {
      return '-'
    }

    const [datePart, timePart] = dateTime?.split(' ');
    const [day, month, year] = datePart?.split('/');
    const [hour, minute, seconds] = timePart?.split(':');

    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, seconds))

    return date
  }

  const getDeviceList = async (onlyBound?: boolean) => {
    let languageId = localStorage.getItem('languageId');

    setLoading(true)

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

      try {
        const res = await axios.get(`${SERVER_URL}/device/list`, { headers });
        const data = res?.data?.data ?? [];

        const currentOnlyBound = localStorage.getItem('onlyBound') ?? onlyBound;
        const liveStatus = localStorage.getItem('liveStatus') === 'true';
        const baseDateRange = getDateRange(statusDevice);

        let deviceOn: any[] = [];
        let devices: any[] = [];

        for (const device of data) {
          if (!device?.isEnabled) continue;

          // --- Normalizzazione ---
          ['temperature', 'battery'].forEach((key) => {
            if (device[key] != null) device[key] = device[key];
          });

          ['dateTime', 'localTime', 'lastPositionDate', 'localLastPositionDate'].forEach((key) => {
            device[key] = convertDateTime(device[key]);
          });

          const localTimeString = device.dateTime?.toLocaleString(languageId, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });

          // --- Calcolo stato ---
          device.status =
            device.dateTime == 0 || compareDateTimes(localTimeString, baseDateRange) !== 1 ? 0 : 1;

          const hasName = !!device?.name;

          // --- LOGICA PRINCIPALE ---
          if (currentOnlyBound === 'true' && liveStatus) {
            // ✅ Se è abilitato e "onlyBound" è true con live attivo
            if (!hasName) {
              // Rimuovi da devices se non ha nome
              devices = devices.filter((d: any) => d.id !== device.id);
              continue;
            }

            // Verifica temporale
            const dateBeforeMinutes = getDateRange(statusDevice);
            const isRecent = compareDateTimes(localTimeString, dateBeforeMinutes) === 1;

            if (isRecent) deviceOn.push(device);
          }
          else if (liveStatus && (currentOnlyBound === 'false' || !currentOnlyBound)) {
            // ✅ Se liveStatus è true ma onlyBound è false o non impostato
            if (!device.dateTime || device.dateTime === 0) continue;

            const dateBeforeMinutes = getDateRange(statusDevice);
            const isRecent = compareDateTimes(localTimeString, dateBeforeMinutes) === 1;

            if (isRecent) deviceOn.push(device);
          }
          else {
            // ✅ Caso liveStatus = false
            if (currentOnlyBound === 'true' && !hasName) continue;
            deviceOn.push(device);
          }
        }

        // --- Aggiornamento stati ---
        // Preserva lo stato inEdit dei dispositivi in modifica
        setDevicesOn((prevDevices: any) => {
          return deviceOn.map((device: any) => {
            const prevDevice = prevDevices.find((d: any) => d.mac === device.mac);
            if (prevDevice?.inEdit) {
              return { ...device, inEdit: true, name: prevDevice.name }; // Mantieni inEdit e il nome in modifica
            }
            return device;
          });
        });
        setCountDevicesOn(deviceOn.length);
        setDevices(devices);

        // --- Pulizia customInfo ---
        for (const device of deviceOn) {
          if (!device.customInfo) continue;
          try {
            const customInfo = JSON.parse(device.customInfo);
            delete customInfo.ProximitySensor;
            delete customInfo.SensorProximity;
            device.customInfo = JSON.stringify(customInfo);
          } catch {
            // Ignora JSON non valido
          }
        }

        loadStateFromLocalStorage();
        setLoading(false);
      } catch (error: any) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(accessTokenRequest);
        }
        console.error('Errore durante il caricamento dei dispositivi:', error);
        setLoading(false);
      }



    }

    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance?.initialize();
      await instance?.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse?.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  //call getDeviceList
  useEffect(() => {
    getDeviceList()
  }, [])

  const editFieldName = async (deviceMac: any, deviceName: any, deviceGroupId: any, deviceJobId: any, startDate: any, deviceSerialNumber: any) => {

    const commonOperations = async (accessToken: any) => {
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      let params = {}
      if (deviceGroupId != 0) {
        params = {
          deviceMac,
          name: deviceName,
          groupId: deviceGroupId,
          jobId: deviceJobId,
          serial: deviceSerialNumber
        }
      } else {
        params = {
          deviceMac,
          name: deviceName,
          serial: deviceSerialNumber
        }
      }

      try {
        await axios.put(`${SERVER_URL}/device/${deviceMac}`, params, { headers })

        const onlyBound: any = JSON.parse(localStorage.getItem('onlyBound') || 'false')
        if (onlyBound) {
          setOnlyBound(onlyBound)
          if (liveStatus) {
            onToggleLive(liveStatus)
          } else {
            getDeviceList(onlyBound)
          }
        } else {
          getDeviceList()
        }

      } catch (error: any) {
        setError(error);
      }
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
        // Acquire token silent success
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
    }
  }

  const IsUnaryFilter = (operator: any) => {
    return ['isnull', 'isnotnull', 'isempty', 'isnotempty'].includes(operator);
  };

  const cellOperatorChange = (event: any, currentValue: any, onChange: any) => {
    let value = currentValue;
    const item = event.target.value;
    if (!event.target.state?.opened) {
      return;
    }
    if (IsUnaryFilter(item.operator)) {
      value = null;
    }
    if (currentValue === null && !IsUnaryFilter(item.operator)) {
      value = undefined;
    }
    onChange({
      value,
      operator: item.operator,
      syntheticEvent: event.syntheticEvent,
    });
  };

  const removeFilterByField = (filterObject: any, fieldName: any): any => {
    if (!filterObject || !filterObject.filters || !Array.isArray(filterObject.filters)) {
      return { logic: 'and', filters: [] };
    }

    const updatedFilters = filterObject.filters
      .map((item: any) => {
        // Se è il filtro da rimuovere
        if (item.field === fieldName) {
          return null;
        }

        // Se è un gruppo di filtri annidato
        if (item.filters && Array.isArray(item.filters)) {
          const updatedGroup = removeFilterByField(item, fieldName);
          // Mantieni solo se il gruppo ha ancora filtri
          if (updatedGroup.filters.length > 0) {
            return updatedGroup;
          } else {
            return null;
          }
        }
        return item;
      })
      .filter((item: any) => item !== null);

    // Sempre restituire un oggetto con 'logic' e 'filters'
    return {
      logic: filterObject.logic || 'and',
      filters: updatedFilters
    };
  };

  const GridColumnMenuFilterCell = (props: any) => {

    const gridColumnMenuFilterCell = useRef<any>(null);

    const handleResetClick = () => {
      const field = props.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);
      setFilterName((prev: any[]) => {
        const updated = prev?.filter((item) => item !== field) ?? [];
        localStorage.setItem('filterNameSynoptic', JSON.stringify(updated));
        return updated;
      });
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    useEffect(() => {
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    useEffect(() => {
      return () => {
        localStorage.removeItem('filterOpenedColumn');
      };
    }, []);

    const [selectedOperator, setSelectedOperator] = useState(props.operator || 'eq');

    const containsDateTimeField = (filters: any): boolean => {
      if (!Array.isArray(filters)) return false;

      return filters.some((filter: any) => {
        if (filter.field === 'dateTime') {
          return true;
        }
        // Se ha sotto-filtri, ricorsivamente cerca anche lì
        if (Array.isArray(filter.filters)) {
          return containsDateTimeField(filter.filters);
        }
        return false;
      });
    };

    const [tempDateTime, setTempDateTime] = useState<Date | any>(null);

    const handleTempDateTimeChange = (value: Date | null) => {
      setTempDateTime(value);
    };

    const operatorChange = (event: any) => {
      const newOperator = event.value.operator;
      setSelectedOperator(newOperator); // <-- Track selected operator
      cellOperatorChange(event, props.value, props.onChange);
    };

    const renderOperatorEditor = () => {
      const { operators } = props;

      const filteredOperators = operators.filter(
        (op: any) => op.operator !== 'isnull' && op.operator !== 'isnotnull'
      );

      const value = filteredOperators.find((item: any) => item.operator === selectedOperator) || null;

      return (
        <DropDownList
          value={value}
          onChange={operatorChange}
          data={filteredOperators}
          textField="text"
        />
      );
    };

    useEffect(() => {
      if (!tempDateTime) return;

      // Serve solo a far vedere a Kendo che c'è un valore
      props.onChange?.({
        field: props.field,
        operator: selectedOperator,
        value: new Date(tempDateTime.setMilliseconds(0))
      });
    }, [tempDateTime]);


    useEffect(() => {
      if (props.value && !tempDateTime) {
        setTempDateTime(new Date(props.value));
      }
    }, [props.value]);

    const applyDateTimeFilter = () => {
      if (!tempDateTime) return;

      const cleanDate = new Date(tempDateTime);
      cleanDate.setMilliseconds(0);

      const newFilter = {
        field: props.field,
        operator: selectedOperator,
        value: cleanDate,
      };

      const existingFilters = dataState?.filter?.filters || [];

      const updatedFilters = existingFilters.filter((f: any) => {
        if (f.filters) {
          return !f.filters.some((inner: any) => inner.field === props.field);
        }
        return f.field !== props.field;
      });

      updatedFilters.push({
        logic: 'or',
        filters: [newFilter],
      });

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilters,
        },
      };

      setDataState(newState);

      props.onChange?.({
        field: props.field,
        operator: selectedOperator,
        value: cleanDate,
      });

      let filterName: any = []
      updatedFilters?.forEach((filter: any) => {
        filter?.filters?.forEach((filterInfo: any) => {
          filterName.push(filterInfo.field)
        })
      })
      setFilterName(filterName)

      localStorage.setItem("filterNameSynoptic", JSON.stringify(filterName));
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');

      getDeviceList();
    };

    useEffect(() => {
      const handleDocumentClick = (e: MouseEvent) => {
        const resetButton = (e.target as HTMLElement).closest('button[type="reset"]');
        const applyButton = (e.target as HTMLElement).closest('button.k-button');

        const resetText = resetButton?.textContent?.trim();
        const applyText = applyButton?.textContent?.trim();

        if (resetText === 'Ripulisci' || resetText === 'Clear') {
          handleResetClick();
        } else if (applyText === 'Filtro' || applyText === 'Filter') {
          applyDateTimeFilter();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, [tempDateTime, selectedOperator]);

    return (
      <div
        ref={gridColumnMenuFilterCell}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ zIndex: 10000, position: 'relative' }}
      >
        {renderOperatorEditor()}

        <DatePicker
          value={tempDateTime}
          format={languageId === 'en-US' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
          formatPlaceholder="formatPattern"
          onChange={(e) => handleTempDateTimeChange(e.value)}
        />

        <div style={{ marginBottom: '10px' }}></div>

        <TimePicker
          value={tempDateTime}
          format={languageId === 'en-US' ? 'hh:mm:ss a' : 'HH:mm:ss'}
          formatPlaceholder="formatPattern"
          onChange={(e) => handleTempDateTimeChange(e.value)}
        />

      </div>
    );
  };

  const CustomGridFilterDateTime = (props: any) => {
    const {
      firstFilterProps
    } = props;

    return (
      <GridColumnMenuFilterCell
        {...firstFilterProps}
      />
    );
  };

  const ColumnMenuCheckboxFilter = (props: any) => {

    useEffect(() => {

      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);

      setFilterName((prev: any[]) => {
        const updated = prev?.filter((item) => item !== field) ?? [];
        localStorage.setItem('filterNameSynoptic', JSON.stringify(updated));
        return updated;
      });
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    //called when the filter is opened
    useEffect(() => {
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    //called when the filter is closed
    useEffect(() => {
      return () => {
        localStorage.removeItem('filterOpenedColumn');
      };
    }, []);


    const handleFilterChange = (filterValue: any) => {

      const newFilter: any = filterValue
        ? { ...filterValue }
        : { logic: 'and', filters: [] };

      const newSkip = dataState.skip ?? 0;
      const newTake = dataState.take ?? 10;

      let filterName: any = []
      filterValue?.filters?.forEach((filter: any) => {
        filter?.filters?.forEach((filterInfo: any) => {
          filterName.push(filterInfo.field)
        })
      })
      setFilterName(filterName)
      localStorage.setItem("filterNameSynoptic", JSON.stringify(filterName));

      if (dataState) {
        const { sort } = dataState;

        setDataState((prevState: any) => ({
          ...prevState,
          filter: newFilter,
          sort: sort,
          skip: 0,
          take: newTake
        }));
      }

      // Salva lo stato aggiornato completo nel localStorage
      const updatedState = {
        dataState: {
          ...dataState,
          filter: newFilter,
          skip: newSkip,
          take: newTake
        },
        columns
      };
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));

    };

    return (
      <div>
        <GridColumnMenuCheckboxFilter
          {...props}
          data={devicesOn}
          expanded={true}
          searchBoxFilterOperator={'contains'}
          onFilterChange={handleFilterChange}
        />
      </div>
    );
  }

  const CustomInfoCheckboxFilter = (props: any) => {

    useEffect(() => {

      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);
      //setFilterName((prev: any) => prev.filter((item: any) => item !== field));
      setFilterName((prev: any) =>
        Array.isArray(prev) ? prev.filter((item: any) => item !== field) : []
      );
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
      localStorage.setItem('filterNameSynoptic', JSON.stringify([]));
    };

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    useEffect(() => {
      return () => {
        localStorage.removeItem('filterOpenedColumn');
      };
    }, []);

    let currDevice: any = []
    devicesOn.map((device: any) => {
      if (device.customInfoDictionary != null && device.customInfoDictionary != '') {
        currDevice.push(device)
      }
    })

    return (
      <div>
        <GridColumnMenuCheckboxFilter
          {...props}
          data={currDevice}
          expanded={true}
          searchBoxFilterOperator={'contains'}
        />
      </div>
    );
  }

  const ColumnMenuNumericFilter = (props: any) => {

    useEffect(() => {
      // Questo effetto scatta all'apertura del filtro
      localStorage.setItem('filterOpenedColumn', 'openedColumn');
    }, []);

    useEffect(() => {
      return () => {
        localStorage.removeItem('filterOpenedColumn');
      };
    }, []);

    useEffect(() => {
      // Delegazione click su bottone reset
      const handleDocumentClick = (e: MouseEvent) => {
        const button = (e.target as HTMLElement).closest('button[type="reset"]');
        if (button) {
          handleResetClick();
        }
      };

      document.body.addEventListener('click', handleDocumentClick);
      return () => document.body.removeEventListener('click', handleDocumentClick);
    }, []);

    const handleResetClick = () => {
      const field = props.column.field;

      // Rimuove il filtro solo per la colonna corrente
      const currentFilter = dataState.filter;
      if (!currentFilter || !Array.isArray(currentFilter.filters)) return;

      // Applichiamo la rimozione ricorsiva
      const updatedFilterObject = removeFilterByField(currentFilter, field);

      const newState = {
        ...dataState,
        filter: {
          logic: 'and',
          filters: updatedFilterObject.filters
        }
      };

      setDataState(newState);

      setFilterName((prev: any[]) => {
        const updated = prev?.filter((item) => item !== field) ?? [];
        localStorage.setItem('filterNameSynoptic', JSON.stringify(updated));
        return updated;
      });

      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
      localStorage.removeItem('filterOpenedColumn');
    };

    if (!props || !props.column) {
      return null;
    }

    const customFilterOperators: any = {
      numeric: [
        { text: 'grid.filterEqOperator', operator: 'eq' },
        { text: 'grid.filterNotEqOperator', operator: 'neq' },
        { text: 'grid.filterGteOperator', operator: 'gte' },
        { text: 'grid.filterGtOperator', operator: 'gt' },
        { text: 'grid.filterLteOperator', operator: 'lte' },
        { text: 'grid.filterLtOperator', operator: 'lt' }

      ]
    };


    const handleFilterChange = (filterValue: any) => {

      const newFilter: any = filterValue
        ? { ...filterValue }
        : { logic: 'and', filters: [] };

      const newSkip = dataState.skip ?? 0;
      const newTake = dataState.take ?? 10;

      let filterName: any = []
      filterValue?.filters?.forEach((filter: any) => {
        filter?.filters?.forEach((filterInfo: any) => {
          filterName.push(filterInfo.field)
        })
      })
      setFilterName(filterName)
      localStorage.setItem("filterNameSynoptic", JSON.stringify(filterName));

      if (dataState) {
        const { sort } = dataState;

        setDataState((prevState: any) => ({
          ...prevState,
          filter: newFilter,
          sort: sort,
          skip: 0,
          take: newTake
        }));
      }

      // Salva lo stato aggiornato completo nel localStorage
      const updatedState = {
        dataState: {
          ...dataState,
          filter: newFilter,
          skip: newSkip,
          take: newTake
        },
        columns
      };
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));

    };
    

    return (
      <GridColumnMenuFilter
        {...props}
        data={devicesOn}
        expanded={true}
        hideSecondFilter={true}
        filterOperators={customFilterOperators}
        onFilterChange={handleFilterChange}
      />
    );
  };

  const StatusCellWithColorComponent = (props: GridCellProps) => {

    const statusDevice: any = props.dataItem.status;
    if (statusDevice == 0) {
      return (
        <td>
          <span className='statusRed'></span>
        </td>
      );
    } else {

      if (statusDevice != 1) {
        return (
          <td>
            <span className='statusRed'></span>
          </td>
        );
      }
      else {
        return (
          <td>
            <span className='statusGreen'></span>
          </td>
        );
      }
    }
  };

  const InChargeCellWithColorComponent = (props: GridCellProps) => {
    const inCharge = props.dataItem.inCharge;
    if (inCharge == null) {
      return (
        <td
          style={{ textAlign: 'center' }}
          title={localizationService.toLanguageString('synoptic_devices.warning_battery', 'NOT_FOUND')}
        >
          <SvgIcon icon={warningTriangleIcon} size="medium" />
        </td>
      );
    }
    else if (inCharge == 0) {
      return (
        <td>
          <span
            className='inChargeRed'
            title={localizationService.toLanguageString('synoptic_devices.inChargeRed', 'NOT_FOUND')}
          ></span>
        </td>
      );
    }
    else {
      return (
        <td>
          <span
            className='inChargeGreen'
            title={localizationService.toLanguageString('synoptic_devices.inChargeGreen', 'NOT_FOUND')}
          ></span>
        </td>
      );
    }
  };

  const GroupCellWithColorComponent = (props: any) => {
    const groupName = props.dataItem.groupName
    const groupColor = props.dataItem.groupColor
    return (
      <td>
        <div style={{ display: 'flex' }}>
          <div className='groupColor' style={{ backgroundColor: groupColor }} />
          <span>{groupName}</span>
        </div>
      </td>
    )
  };

  const JobCellWithColorComponent = (props: any) => {
    const jobName = props.dataItem.jobName
    const jobColor = props.dataItem.jobColor
    return (
      <td>
        <div style={{ display: 'flex' }}>
          <div className='jobColor' style={{ backgroundColor: jobColor }} />
          <span style={{ minWidth: '100px' }}>{jobName}</span>
        </div>
      </td>
    )
  };

  const TemperatureCellWithBackGroundComponent = (props: any) => {
    const temperatureValue = props?.dataItem?.temperature;
    if (temperatureValue != null) {
      return (
        <td style={{ textAlign: 'center' }}>
          <span className='k-table-row k-master-row temperature'>{temperatureValue} °C</span>
        </td>
      )
    } else {
      return (
        <td style={{ textAlign: 'center' }}><span className='k-table-row k-master-row temperature' /></td>
      )
    }
  };

  const DateTimeCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.dateTime);
    if (isNaN(new Date(date).getTime())) {
      return (<td><span className='k-table-row k-master-row date-time' /></td>)
    }
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const dateTimeValue: any = formatDateFromIsoToString(date);
    if (dateTimeValue != null && !dateTimeValue?.includes('1970')) {
      return (
        <td>
          <span className='k-table-row k-master-row date-time'>
            {dateTimeValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span className='k-table-row k-master-row date-time' /></td>
      )
    }
  };

  const LastPositionDateCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.lastPositionDate);
    if (isNaN(new Date(date).getTime())) {
      return (<td><span className='k-table-row k-master-row date-time' /></td>)
    }
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const lastPositionDateValue: any = formatDateFromIsoToString(date);
    if (lastPositionDateValue != null && !lastPositionDateValue.includes('1970') && !lastPositionDateValue.includes('1753')) {
      return (
        <td>
          <span className='k-table-row k-master-row last-position'>
            {lastPositionDateValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span className='k-table-row k-master-row last-position' /></td>
      )
    }
  };

  const LocalLastPositionDateCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.localLastPositionDate);
    if (isNaN(new Date(date).getTime())) {
      return (<td><span className='k-table-row k-master-row date-time' /></td>)
    }
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const localLastPositionDateValue: any = formatDateFromIsoToString(props?.dataItem?.localLastPositionDate);
    if (localLastPositionDateValue != null && !localLastPositionDateValue.includes('1970') && !localLastPositionDateValue.includes('1753')) {
      return (
        <td>
          <span className='k-table-row k-master-row local-last-position'>
            {localLastPositionDateValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span className='k-table-row k-master-row local-last-position' /></td>
      )
    }
  };

  const LocalTimeCellWithBackGroundComponent = (props: any) => {
    const date = new Date(props?.dataItem?.localTime);
    if (isNaN(new Date(date).getTime())) {
      return (<td><span className='k-table-row k-master-row date-time' /></td>)
    }
    const meridian = date.toLocaleString('en-US', { hour: 'numeric', hour12: true }).split(' ')[1];
    const localTimeValue: any = formatDateFromIsoToString(props?.dataItem?.localTime);
    if (localTimeValue != null && !localTimeValue.includes('1970') && !localTimeValue.includes('1753')) {
      return (
        <td>
          <span className='k-table-row k-master-row local-time'>
            {localTimeValue} {languageId === 'it-IT' ? '' : meridian}
          </span>
        </td>
      )
    } else {
      return (
        <td><span className='k-table-row k-master-row local-time' /></td>
      )
    }
  };

  const BatteryCellWithBackGroundComponent = (props: any) => {
    const batteryValue = props.dataItem.battery;
    //const batteryValueWithoutPercentage = batteryValue.trim().replace('%', '');

    if (!batteryValue) {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>?</title>
            <g transform="rotate(90 32 32)">
              <g fill="black">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="black">
                <text
                  x="35"
                  y="45"
                  textAnchor="middle"
                  fontSize="20"
                  fontWeight="bold"
                  fill="black"
                  transform="rotate(-90 32 38)"
                >
                  ?
                </text>
              </g>
            </g>
          </svg>
        </td>
      )
    }
    else if (batteryValue < 25) {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="red">
                {/*<path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>*/}
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    }
    else if (batteryValue >= 25 && batteryValue <= 50) {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="yellow">
                {/*<path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>*/}
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    } else if (batteryValue > 50 && batteryValue <= 75) {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="lightgreen">
                {/*<path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>*/}
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    } else {
      return (
        <td>
          <svg className="battery-svg" width="40px" height="40px" viewBox="0 0 64 64">
            <title>{batteryValue}%</title>
            <g transform="rotate(90 32 32)">
              <g fill="#3e4347">
                <path d="M42 7c0 1.1-.9 2-2 2H24c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v3"></path>
                <path d="M48 6H16c-2.2 0-4 1.8-4 4v48c0 2.2 1.8 4 4 4h32c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4m0 47c0 .5-.5 1-1 1H17c-.5 0-1-.5-1-1V15c0-.5.5-1 1-1h30c.5 0 1 .5 1 1v38"></path>
              </g>
              <g fill="green">
                <path d="M46 24c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 36c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
                <path d="M46 48c0 1.1-.9 2-2 2H20c-1.1 0-2-.9-2-2v-4c0-1.1.9-2 2-2h24c1.1 0 2 .9 2 2v4"></path>
              </g>
            </g>
          </svg>
        </td>
      )
    }
  };

  const AlarmCellWithBackGroundComponent = (props: any) => {
    const alarmValue = props.dataItem.hasAlarm;

    const navigate = useNavigate()

    const showAlarm = () => {
      let deviceMac = props?.dataItem?.mac;
      if (deviceMac != null) {
        localStorage.setItem("deviceMacFromSynopticInAlarm", deviceMac);
      }
      navigate("/home/alarms")
    }

    if (alarmValue) {
      return (
        <td className="k-command-cell" style={{ textAlign: 'center' }}>
          <button
            style={{ color: '#198792', border: 'none', background: 'none', outline: 'none' }}
            onClick={showAlarm}
          >
            <SvgIcon icon={alarm} className={'alarmButtonSynoptic'} size="medium" />
          </button>
        </td>
      )
    }
    else {
      return (
        <td>
          <span></span>
        </td>
      )
    }
  };

  const defaultColumns: any = [
    { field: 'status', title: localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND'), editable: false, filterable: false, show: true, exportable: false },
    { field: 'mac', title: localizationService.toLanguageString('synoptic_devices.mac', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, editable: false, show: true, exportable: false },
    { field: 'serialNumber', title: localizationService.toLanguageString('synoptic_devices.serialNumber', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'name', title: localizationService.toLanguageString('synoptic_devices.name', 'NOT_FOUND'), editor: "text", columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: true, show: true, hidden: false, exportable: false },
    { field: 'groupName', title: localizationService.toLanguageString('synoptic_devices.groupName', 'NOT_FOUND'), columnMenu: GroupCellWithColorComponent, filterable: false, editable: false, show: true, hidden: false, exportable: false },
    { field: 'jobName', title: localizationService.toLanguageString('synoptic_devices.jobName', 'NOT_FOUND'), columnMenu: JobCellWithColorComponent, filterable: false, editable: false, show: true, hidden: false, exportable: false },
    { field: 'zoneNameListJoined', title: localizationService.toLanguageString('synoptic_devices.zoneNameListJoined', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: false, editable: false, show: true, hidden: false, exportable: false },
    { field: 'deviceTypeName', title: localizationService.toLanguageString('synoptic_devices.deviceTypeName', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, editable: false, show: true, hidden: false, exportable: false },
    { field: 'temperature', title: localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND'), filter: "numeric", filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'localTime', title: localizationService.toLanguageString('synoptic_devices.localTime', 'NOT_FOUND'), format: `${languageId == 'en-US' ? '{0:MM/dd/yyyy hh:mm:ss a}' : '{0:dd/MM/yyyy HH:mm:ss}'}`, filter: "date", filterable: true, editable: false, show: false, hidden: false, exportable: false },
    { field: 'dateTime', title: localizationService.toLanguageString('synoptic_devices.dateTime', 'NOT_FOUND'), format: `${languageId == 'en-US' ? '{0:MM/dd/yyyy hh:mm:ss a}' : '{0:dd/MM/yyyy HH:mm:ss}'}`, filter: "date", filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'lastPositionDate', title: localizationService.toLanguageString('synoptic_devices.lastPositionDate', 'NOT_FOUND'), format: `${languageId == 'en-US' ? '{0:MM/dd/yyyy hh:mm:ss a}' : '{0:dd/MM/yyyy HH:mm:ss}'}`, filter: "date", filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'localLastPositionDate', title: localizationService.toLanguageString('synoptic_devices.localLastPositionDate', 'NOT_FOUND'), format: `${languageId == 'en-US' ? '{0:MM/dd/yyyy hh:mm:ss a}' : '{0:dd/MM/yyyy HH:mm:ss}'}`, filter: "date", filterable: true, editable: false, show: false, hidden: false, exportable: false },
    { field: 'customInfo', title: localizationService.toLanguageString('synoptic_devices.customInfo', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: true, hidden: false, exportable: false },
    { field: 'battery', title: localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND'), cell: BatteryCellWithBackGroundComponent, filterable: true, filter: "numeric", editable: false, show: true, exportable: false },
    { field: 'inCharge', title: localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND'), filterable: false, editable: false, show: true, exportable: false },
    { field: 'hasAlarm', title: localizationService.toLanguageString('synoptic_devices.hasAlarm', 'NOT_FOUND'), cell: AlarmCellWithBackGroundComponent, filterable: false, editable: false, show: true, exportable: false },
    { field: 'technology', title: localizationService.toLanguageString('synoptic_devices.technology', 'NOT_FOUND'), columnMenu: ColumnMenuCheckboxFilter, filterable: true, editable: false, show: false, exportable: true },
    //...customInfoDictionaryColumns
  ];

  const [dataState, setDataState] = useState<any>({
    skip: 0,
    take: 10,
    data: devices,
    total: devices?.length,
    filter: { logic: 'and', filters: [] },
    //sort: [{ field: 'mac', dir: 'desc' }]
  });

  const [originalNames, setOriginalNames] = useState<Record<string, string>>({});

  //used when update device name
  const update = (dataItem: any) => {

    dataItem.inEdit = false;

    const devicesOnCopy = [...devicesOn];

    let index: any = devicesOn.findIndex((record: any) => record.mac === dataItem.mac);
    devicesOnCopy[index] = dataItem;

    var currentDate = new Date()
    var currentTime = formatDateFromIsoToString(currentDate)
    editFieldName(dataItem.mac, dataItem.name, dataItem.groupId, dataItem.jobId, currentTime, dataItem.serialNumber);
    setDevicesOn(devicesOnCopy);

    // Rimuovi il filtro "name" se presente dopo il rename
    const currentFilterName = JSON.parse(localStorage.getItem("filterNameSynoptic") || "[]");
    const updatedFilterName = currentFilterName.filter((item: string) => (
      item !== 'name' && item !== 'nameEdit'
    ));
    localStorage.setItem("filterNameSynoptic", JSON.stringify(updatedFilterName));
    setFilterName(updatedFilterName);

    // Rimuovi anche il filtro "name" dal dataState
    if (dataState?.filter?.filters) {
      const updatedFilter = removeFilterByField(dataState.filter, 'name');
      const newState = {
        ...dataState,
        filter: updatedFilter
      };
      setDataState(newState);
      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify({ dataState: newState, columns }));
    }

    setOriginalNames(prev => ({ ...prev, [dataItem.mac]: dataItem.name }));
    setEdit((edit: any) => ({ ...edit, [dataItem.mac!]: false }));
    setEditMode(false)
  };

  const cancel = (dataItem: any) => {
    setDevicesOn((prev: any) =>
      prev.map((d: any) =>
        d.mac === dataItem.mac
          ? { ...d, name: originalNames[dataItem.mac], inEdit: false } // ripristina vecchio nome e chiude edit
          : d
      )
    );
    setEdit((edit: any) => ({ ...edit, [dataItem.mac!]: false }));
    setEditMode(false)
  };

  const enterEdit = (dataItem: any) => {
    setEditMode(true);

    const newDevice = devicesOn.map((item: any) =>
      item.mac === dataItem.mac
        ? { ...item, inEdit: true }
        : item
    );

    // Legge da localStorage e aggiorna in modo sicuro
    const currentFilter = JSON.parse(localStorage.getItem("filterNameSynoptic") || "[]");

    // Aggiunge 'nameEdit' solo se non esiste già
    const updatedFilter = Array.from(new Set([...currentFilter, 'nameEdit']));

    localStorage.setItem("filterNameSynoptic", JSON.stringify(updatedFilter));
    setFilterName(updatedFilter);

    setDevicesOn(newDevice);
    setOriginalNames(prev => ({ ...prev, [dataItem.mac]: dataItem.name }));
    setEdit((edit: any) => ({ ...edit, [dataItem.mac!]: true }));
  };

  const itemChange = (event: GridItemChangeEvent) => {
    setDevicesOn((prevDevices: any) =>
      prevDevices.map((device: any) =>
        device.mac === event.dataItem.mac
          ? { ...device, [event.field || ""]: event.value }
          : device
      )
    );
  };

  const commandCellProps = {
    edit: enterEdit,
    update: update,
    cancel: cancel,
    editField: editField
  };

  const saveStateToLocalStorage = () => {
    const state = { dataState, columns };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(state));
  };

  const loadStateFromLocalStorage = () => {

    const storedFilter = localStorage.getItem(`gridSynopticState: ${userEmail}`);
    if (storedFilter) {
      const { dataState: savedDataState, columns: savedColumns } = JSON.parse(storedFilter);

      savedColumns.forEach((column: any) => {
        column.title = localizationService.toLanguageString(`synoptic_devices.${column.field}`, 'NOT_FOUND');
      })

      if (savedDataState?.filter != null) {
        // Funzione ricorsiva per gestire qualsiasi struttura di filtri
        const updateFilterValues = (filter: any): any => {
          if (Array.isArray(filter.filters)) {
            return {
              ...filter,
              filters: filter.filters.map(updateFilterValues),
            };
          } else {
            const field = filter?.field;
            let value = filter?.value;

            if (['dateTime', 'localTime', 'localLastPositionDate', 'lastPositionDate'].includes(field)) {
              value = new Date(value);
            }

            return {
              ...filter,
              value,
            };
          }
        };

        const updatedFilter = updateFilterValues(savedDataState.filter);

        const updatedSavedDataState = {
          ...savedDataState,
          filter: updatedFilter,
        };

        setDataState(updatedSavedDataState);
      } else {
        setDataState(savedDataState);
      }

      if (visibleCountSynoptic === 0 && !firstLoad) {

        const storedState = JSON.parse(localStorage.getItem(`gridSynopticState: ${userEmail}`) || "{}");

        const updatedState = {
          ...storedState,
          dataState: {
            ...storedState.dataState,
            skip: 0
          }
        };

        localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));
      }

      setFirstLoad(false)


      // Column management remains unchanged
      let currentColumn: any = savedColumns?.map((column: any, id: any) => {
        if (column.id == id) {
          return { ...column, show: !column.show };
        }
        return column;
      });

      setColumns(currentColumn)

      return storedFilter ? storedFilter : currentColumn;
    } else {
      setColumns(defaultColumns)
    }
  };

  //toolbar buttons functions
  const onChange = (event: any) => {
    const field = event.target.name;
    const newColumns = columns.map((column: any) => {
      if (column.field === field) {
        return { ...column, show: !column.show };
      }
      return column;
    });
    setColumns(newColumns);

    const state = { dataState, columns: newColumns };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(state));
  };

  const handleColumnReorder = (event: any) => {
    setColumns(event?.columns);
    saveStateToLocalStorage();
  };

  const onColumnResize = (event: GridColumnResizeEvent) => {
    setColumns(event?.columns);
    saveStateToLocalStorage();
  };

  const onDataStateChange = (event: GridDataStateChangeEvent) => {

    const newFilter: any = event.dataState.filter
      ? { ...event.dataState.filter }
      : { logic: 'and', filters: [] };

    let filterName: any = []
    newFilter?.filters?.forEach((filter: any) => {
      filter?.filters?.forEach((filterInfo: any) => {
        filterName.push(filterInfo.field)
      })
    })
    setFilterName(filterName)
    localStorage.setItem("filterNameSynoptic", JSON.stringify(filterName));

    const { dataState } = event;
    if (dataState) {
      const { filter, sort, skip, take } = dataState;
      const updatedFilter = filter || dataState.filter;
      const updatedSkip = skip !== undefined ? skip : dataState.skip;
      const updatedTake = take !== undefined ? take : dataState.take;
      setDataState((prevState: any) => ({
        ...prevState,
        filter: updatedFilter,
        sort: sort,
        skip: updatedSkip,
        take: updatedTake
      }));
    }
    const state = { dataState, columns };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(state));
  };

  const CellRender = (props: any) => {
    return (
      <td
        {...props.tdProps}
        colSpan={1}
      >
        {props.children}
      </td>
    );
  }

  const sanitizeFilter = (filter: any) => {
    if (filter && typeof filter === 'object' && Array.isArray(filter.filters)) {
      const flatFilters = filter.filters.flatMap((f: any) =>
        Array.isArray(f.filters) ? f.filters : [f]
      );

      return {
        logic: filter.logic || 'and',
        filters: flatFilters
      };
    }
    return { logic: 'and', filters: [] };
  };

  const safeFilter = sanitizeFilter(dataState?.filter);

  const [processedData, setProcessedData] = useState<any>({ data: [], total: 0 });

  useEffect(() => {
    //if (!devicesOn || devicesOn.length === 0) return;

    const result = kendoProcess(devicesOn, {
      filter: dataState?.filter ?? safeFilter,
      sort: dataState?.sort,
      skip: dataState?.skip,
      take: dataState?.take,
    });

    setProcessedData(result);

    if (result.data.length === 0 && result.total > 0 && dataState.skip > 0 && !firstLoad) {
      setDataState((prev: any) => ({ ...prev, skip: 0 }));
    }
  }, [devicesOn, dataState?.filter, dataState?.sort, dataState?.skip, dataState?.take]);



  const onPageChange = (event: any) => {

    const targetEvent = event.targetEvent;
    const newSkip = event?.page?.skip;
    const newTake = event?.targetEvent?.value === 'All' ? devicesOn?.length : event.page.take;
    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    const newDataState = {
      ...dataState,
      skip: newSkip,
      take: newTake
    };

    setDataState(newDataState);

    // Calcola manualmente la porzione visibile
    const processedPage = kendoProcess(devicesOn, newDataState);
    const visibleNow = processedPage.data.length;
    setVisibleCount(visibleNow);
    localStorage.setItem("visibleCountSynoptic", JSON.stringify(visibleNow));
    // Salva lo stato aggiornato completo nel localStorage
    const updatedState = {
      dataState: {
        ...dataState,
        skip: newSkip,
        take: newTake
      },
      columns
    };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));
  };

  const _excelExport = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_excelExport.current !== null) {
      _excelExport?.current?.save(devicesOn);
    }
  };

  const _pdfExport = useRef<GridPDFExport | null>(null);
  const pdfExport = () => {
    if (_pdfExport.current !== null) {
      _pdfExport?.current?.save(devicesOn);
    }
  };

  const visibleColumns = useMemo(() => columns?.filter((col: any) => col?.show), [columns]);

  const handleExportCsv = async () => {

    const filteredData: any = devicesOn.map((item: any) => {
      const filteredItem: any = {};
      visibleColumns.forEach((col: any) => {
        filteredItem[col.field] = item[col.field];
      });
      return filteredItem;
    });

    try {
      const csv = await json2csv(filteredData);

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "data.csv");
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    }
  };


  const onToggleLive = (e: any) => {

    const liveStatus: any = e.value ?? e
    localStorage.setItem('liveStatus', JSON.stringify(liveStatus))
    setLiveStatus(liveStatus)

    const onlyBound: any = localStorage.getItem('onlyBound')

    if (liveStatus) {
      const minutesBefore: any = statusDevice

      let liveDevicesToShowInGrid: any = []
      let dateBeforeMinutes: any = ''

      //devicesList[0].lastPositionDate = '12/04/2024 8:00:00 AM' // for test
      devicesOn.map((device: any) => {
        if (device.dateTime == null || device.dateTime == 0) {
          return
        } else {
          dateBeforeMinutes = getDateRange(minutesBefore)
          const localTimeString = device.dateTime.toLocaleString(languageId, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            //hour12: true // Force 12-hour format with AM/PM
          });
          if (compareDateTimes(localTimeString, dateBeforeMinutes) == 1) {
            liveDevicesToShowInGrid.push(device)
          }
        }
      })
      setDevicesOn(liveDevicesToShowInGrid)

      if (onlyBound == 'true') {
        getDeviceList(onlyBound)
      } else {
        getDeviceList()
      }
    } else {
      if (onlyBound == 'true') {
        getDeviceList(onlyBound)
      } else {
        getDeviceList()
      }
    }

    const storedState = JSON.parse(localStorage.getItem(`gridSynopticState: ${userEmail}`) || "{}");

    if (visibleCountSynoptic === 0 && !firstLoad) {

      const updatedState = {
        ...storedState,
        dataState: {
          ...storedState.dataState,
          skip: 0
        }
      };

      localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));
    }

  }

  const getDateRange = (minutes: any) => {
    // Create a Date object from the input string
    const currentDate = new Date();

    // 2. Calculate milliseconds to subtract:
    const millisecondsToSubtract = minutes * 60 * 1000; // minutes * seconds * milliseconds

    // 3. Subtract the milliseconds:
    currentDate.setTime(currentDate?.getTime() - millisecondsToSubtract);

    const localTimeString = currentDate.toLocaleString(languageId, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      //hour12: true // Force 12-hour format with AM/PM
    });

    return localTimeString
  }

  const convertToDate = (dateString: string, isItalian: boolean = false): Date | null => {
    if (!dateString) return null;

    try {
      const cleanStr = dateString.replace(/,/g, '').trim();
      const parts = cleanStr.split(' ');

      let day: number, month: number, year: number;
      let hours: number = 0, minutes: number = 0, seconds: number = 0;

      if (isItalian) {
        // Formato italiano: "dd/MM/yyyy HH:mm:ss"
        const [datePart, timePart] = parts;
        if (!datePart || !timePart) return null;

        [day, month, year] = datePart.split('/').map(Number);
        [hours, minutes, seconds] = timePart.split(':').map(Number);
      } else {
        // Formato inglese: "MM/dd/yyyy hh:mm:ss AM|PM"
        if (parts.length < 3) return null;

        const [datePart, timePart, ampm] = parts;
        [month, day, year] = datePart.split('/').map(Number);
        [hours, minutes, seconds] = timePart.split(':').map(Number);

        if (ampm === 'PM' && hours < 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;
      }

      if ([day, month, year, hours, minutes, seconds].some(n => isNaN(n))) return null;

      const date = new Date(year, month - 1, day, hours, minutes, seconds);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  const compareDateTimes = (date1Str: any, date2Str: any) => {

    let currLanguage = languageId
    if (currLanguage == 'it-IT') {
      currLanguage = true
    } else {
      currLanguage = false
    }
    // Parse the dates into Date objects    
    const date1: any = convertToDate(date1Str, currLanguage);
    const date2: any = convertToDate(date2Str, currLanguage);

    // Compare the milliseconds
    if (date1?.getTime() < date2?.getTime()) {
      return -1; // date1 is earlier
    } else if (date1?.getTime() > date2?.getTime()) {
      return 1; // date1 is later
    } else {
      return 0; // dates are equal
    }
  }

  const onToggleOnlyBound = (e: any) => {
    const onlyBound: any = e.value
    localStorage.setItem('onlyBound', JSON.stringify(onlyBound))
    setOnlyBound(onlyBound)
    if (liveStatus) {
      onToggleLive(liveStatus)
    } else {
      getDeviceList(onlyBound)
    }
    const newSkip = 0;
    const newTake = 10;
    setDataState((prevState: any) => ({
      ...prevState,
      skip: newSkip,
      take: dataState.take ?? newTake
    }));
    const updatedState = {
      dataState: {
        ...dataState,
        skip: newSkip,
        take: dataState.take ?? newTake
      },
      columns
    };
    localStorage.setItem(`gridSynopticState: ${userEmail}`, JSON.stringify(updatedState));
    //getDeviceList(onlyBound)
  }

  const IconHeaderCell = (props: any) => {
    localStorage.removeItem('filterOpenedColumn');
    const savedFilterName: any = localStorage.getItem('filterNameSynoptic');
    const parsedFilterName = savedFilterName ? JSON.parse(savedFilterName) : [];
    
    // Mostra l'icona solo se il campo è nei filtri E non è 'nameEdit'
    const shouldShowIcon = parsedFilterName.includes(props.field) && !parsedFilterName.includes('nameEdit');

    return shouldShowIcon ?
      <HeaderThElement columnId={props.thProps?.columnId || ''} {...props.thProps} >
        <span style={{ display: 'inline-flex' }}>{props.children} <SvgIcon icon={filterIcon} size="medium" /></span>
      </HeaderThElement>
      :
      <HeaderThElement columnId={props.thProps?.columnId || ''} {...props.thProps} >
        {props.children}
      </HeaderThElement>
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'grid',
        width: '98%',
        overflowY: 'auto',
        margin: '10px',
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 15px 5px'
      }}
    >
      {error && <p className="text-danger" style={{ zIndex: 999, position: 'relative', width: '65%' }}>{error}</p>}
      {loading && loadingPanel}

      <ExcelExport data={devicesOn} ref={_excelExport}>
        {columns
          .map((col: any) => (
            <ExcelExportColumn
              key={col.field}
              field={col.field}
              title={localizationService.toLanguageString(`synoptic_devices.${col.field}`, col.title)}
              cellOptions={{
                format:
                  col.field.includes('dateTime') || col.field.includes('localTime') || col.field.includes('localLastPositionDate') || col.field.includes('lastPositionDate')
                    ? languageId === 'en-US'
                      ? 'MM/dd/yyyy hh:mm:ss AM/PM'
                      : 'dd/MM/yyyy HH:mm:ss'
                    : undefined
              }}
            />
          ))}
      </ExcelExport>

      <Grid
        data={processedData}
        dataItemKey={"mac"}
        cells={{ data: CellRender }}
        sortable={true}
        take={dataState?.take ?? 10}
        skip={dataState?.skip ?? 0}
        pageable={{ type: 'input', buttonCount: 5, pageSizes: [5, 10, 50, 100, 'All'], pageSizeValue: pageSizeValue, info: true }}
        reorderable={false}
        sort={dataState?.sort}
        filter={dataState?.filter}
        onColumnResize={onColumnResize}
        onDataStateChange={onDataStateChange}
        onPageChange={onPageChange}
        onItemChange={itemChange}
        edit={edit}
        editable={true}
        scrollable={'none'}
      >

        <GridToolbar>
          <div>
            <h3>{localizationService.toLanguageString('synoptic_devices.title', 'NOT_FOUND')}</h3>
          </div>
          <div style={{ marginLeft: '25px', whiteSpace: 'nowrap', alignItems: 'flex-end' }}>
            <Switch
              onChange={onToggleLive}
              checked={liveStatus}
            />
            <label style={{ margin: '0 auto' }} className="k-ml-3">{localizationService.toLanguageString('synoptic_devices.live', 'NOT_FOUND')}</label>
          </div>
          <div style={{ marginLeft: '25px', whiteSpace: 'nowrap', alignItems: 'flex-end' }}>
            <Switch
              onChange={onToggleOnlyBound}
              checked={onlyBound}
            />
            <label style={{ margin: '0 auto' }} className="k-ml-3">{localizationService.toLanguageString('synoptic_devices.only_binded', 'NOT_FOUND')}</label>
          </div>

          <div style={{ right: '15px', position: 'absolute', display: 'flex', gap: '8px' }}>
            <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
            <div>
              <Button
                themeColor={"primary"}
                size="small"
                title={localizationService.toLanguageString('synoptic_devices.export_to_csv', 'NOT_FOUND')}
                svgIcon={fileCsvIcon}
                onClick={handleExportCsv}
              />
            </div>
            <div>
              <Button
                onClick={pdfExport}
                themeColor={"primary"}
                size="small"
                title={localizationService.toLanguageString('synoptic_devices.export_to_pdf', 'NOT_FOUND')}
                svgIcon={filePdfIcon}
              />
            </div>
            <div>
              <Button
                title={localizationService.toLanguageString('synoptic_devices.export_to_excel', 'NOT_FOUND')}
                themeColor={"primary"}
                size="small"
                svgIcon={fileExcelIcon}
                onClick={excelExport}
              />
            </div>
          </div>
        </GridToolbar>
        {columns[2]?.show &&
          <Column
            cells={{ data: (props: any) => <EditingCell  {...props} {...commandCellProps} /> }}
            filterable={false}
            sortable={false}
          />
        }
        <Column
          cells={{ data: LinkMapCell }}
          editable={false}
          filterable={false}
          sortable={false}
        />
        <Column
          cells={{ data: LinkDeviceManagement }}
          editable={false}
          filterable={false}
          sortable={false}
        />
        {
          columns?.length > 0 ?
            columns?.map((column: any, index: any) =>
              column?.show && (
                column.field == 'groupName' ?
                  <Column
                    field={column.field}
                    title={localizationService.toLanguageString('synoptic_devices.groupName', 'NOT_FOUND')}
                    cells={{
                      data: (props) => <GroupCellWithColorComponent  {...props} color={column?.groupColor} />,
                      headerCell: IconHeaderCell
                    }}
                    editable={false}
                    filterable={true}
                    columnMenu={ColumnMenuCheckboxFilter}
                    sortable={true}
                  />
                  :
                  column.field == 'jobName' ?
                    <Column
                      field={column.field}
                      title={localizationService.toLanguageString('synoptic_devices.jobName', 'NOT_FOUND')}
                      cells={{
                        data: (props) => <JobCellWithColorComponent  {...props} color={column?.jobColor} />,
                        headerCell: IconHeaderCell
                      }}
                      editable={false}
                      filterable={true}
                      columnMenu={ColumnMenuCheckboxFilter}
                      sortable={true}
                    />
                    :
                    column.field == 'temperature' ?
                      <Column
                        field={column.field}
                        title={localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND')}
                        cells={{
                          data: (props) => <TemperatureCellWithBackGroundComponent  {...props} />,
                          headerCell: IconHeaderCell
                        }}
                        editable={false}
                        filterable={true}
                        columnMenu={ColumnMenuCheckboxFilter}
                        filter='numeric'
                        sortable={true}
                      />
                      :
                      column.field == 'dateTime' ?
                        <Column
                          field={column.field}
                          title={localizationService.toLanguageString('synoptic_devices.dateTime', 'NOT_FOUND')}
                          cells={{
                            data: (props) => <DateTimeCellWithBackGroundComponent  {...props} />,
                            headerCell: IconHeaderCell,
                          }}
                          editable={false}
                          columnMenu={(props) => (
                            <GridColumnMenuFilter
                              {...props}
                              alwaysExpand
                              filterUI={CustomGridFilterDateTime}
                              onExpandChange={() => { }}
                              onCloseMenu={() => {
                                const filterName = props.column.field;
                                setFilterName((prevFilterName: any) => {
                                  return prevFilterName.filter((item: any) => item !== filterName);
                                });
                                localStorage.removeItem('filterOpenedColumn');
                              }}
                            />
                          )}
                          filter={'date'}
                          format={languageId == 'en-US' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss'}
                          sortable={true}
                        />
                        :
                        column.field == 'lastPositionDate' ?
                          <Column
                            field={column.field}
                            title={localizationService.toLanguageString('synoptic_devices.lastPositionDate', 'NOT_FOUND')}
                            cells={{
                              data: (props) => <LastPositionDateCellWithBackGroundComponent  {...props} />,
                              headerCell: IconHeaderCell
                            }}
                            editable={false}
                            columnMenu={(props) => (
                              <GridColumnMenuFilter
                                {...props}
                                alwaysExpand
                                filterUI={CustomGridFilterDateTime}
                                onExpandChange={() => { }}
                                onCloseMenu={() => {
                                  const filterName = props.column.field;
                                  setFilterName((prevFilterName: any) => {
                                    return prevFilterName.filter((item: any) => item !== filterName);
                                  });
                                  localStorage.removeItem('filterOpenedColumn');
                                }}
                              />
                            )}
                            filter={'date'}
                            format={languageId == 'en-US' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss'}
                            sortable={true}
                          />
                          :
                          column.field == 'localLastPositionDate' ?
                            <Column
                              field={column.field}
                              title={localizationService.toLanguageString('synoptic_devices.localLastPositionDate', 'NOT_FOUND')}
                              cells={{
                                data: (props) => <LocalLastPositionDateCellWithBackGroundComponent  {...props} />,
                                headerCell: IconHeaderCell
                              }}
                              editable={false}
                              //format={column.format}
                              columnMenu={(props) => (
                                <GridColumnMenuFilter
                                  {...props}
                                  alwaysExpand
                                  filterUI={CustomGridFilterDateTime}
                                  onExpandChange={() => { }}
                                  onCloseMenu={() => {
                                    const filterName = props.column.field;
                                    setFilterName((prevFilterName: any) => {
                                      return prevFilterName.filter((item: any) => item !== filterName);
                                    });
                                    localStorage.removeItem('filterOpenedColumn');
                                  }}
                                />
                              )}
                              filter={'date'}
                              format={languageId == 'en-US' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss'}
                              sortable={true}
                            />
                            :
                            column.field == 'localTime' ?
                              <Column
                                field={column.field}
                                title={localizationService.toLanguageString('synoptic_devices.localTime', 'NOT_FOUND')}
                                cells={{
                                  data: (props) => <LocalTimeCellWithBackGroundComponent  {...props} />,
                                  headerCell: IconHeaderCell
                                }}
                                editable={false}
                                columnMenu={(props) => (
                                  <GridColumnMenuFilter
                                    {...props}
                                    alwaysExpand
                                    filterUI={CustomGridFilterDateTime}
                                    onExpandChange={() => { }}
                                    onCloseMenu={() => {
                                      const filterName = props.column.field;
                                      setFilterName((prevFilterName: any) => {
                                        return prevFilterName.filter((item: any) => item !== filterName);
                                      });
                                      localStorage.removeItem('filterOpenedColumn');
                                    }}
                                  />
                                )}
                                filter={'date'}
                                format={languageId == 'en-US' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss'}
                                sortable={true}
                              />
                              :
                              column.field == 'customInfo' ?
                                <Column
                                  field={column.field}
                                  title={localizationService.toLanguageString('synoptic_devices.customInfo', 'NOT_FOUND')}
                                  columnMenu={CustomInfoCheckboxFilter}
                                  editable={false}
                                  filterable={true}
                                  format={column.format}
                                  filter={column.filter}
                                  sortable={true}
                                  cells={{ headerCell: IconHeaderCell }}
                                />
                                :
                                column.field == 'battery' ?
                                  <Column
                                    field={column.field}
                                    title={localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND')}
                                    cells={{
                                      data: (props) => <BatteryCellWithBackGroundComponent  {...props} />,
                                      headerCell: IconHeaderCell
                                    }}
                                    editable={false}
                                    filterable={true}
                                    filter='numeric'
                                    columnMenu={ColumnMenuNumericFilter}
                                    sortable={true}
                                  />
                                  :
                                  column.field == 'status' ?
                                    <Column
                                      field={column.field}
                                      title={localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND')}
                                      cells={{
                                        data: (props) => <StatusCellWithColorComponent  {...props} />,
                                        headerCell: IconHeaderCell
                                      }}
                                      editable={false}
                                      filterable={false}
                                      sortable={true}
                                    />
                                    :
                                    column.field == 'inCharge' ?
                                      <Column
                                        field={column.field}
                                        title={localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND')}
                                        cells={{
                                          data: (props) => <InChargeCellWithColorComponent  {...props} />,
                                          headerCell: IconHeaderCell
                                        }}
                                        editable={false}
                                        filterable={false}
                                        sortable={true}
                                      />
                                      :
                                      column.field == 'hasAlarm' ?
                                        <Column
                                          field={column.field}
                                          title={localizationService.toLanguageString('synoptic_devices.hasAlarm', 'NOT_FOUND')}
                                          cells={{
                                            data: (props) => <AlarmCellWithBackGroundComponent  {...props} />,
                                            headerCell: IconHeaderCell
                                          }}
                                          editable={false}
                                          filterable={false}
                                          sortable={true}
                                        />
                                        :
                                        column.field == 'technology' ?
                                          <Column
                                            field={column.field}
                                            title={localizationService.toLanguageString('synoptic_devices.technology', 'NOT_FOUND')}
                                            cells={{ headerCell: IconHeaderCell }}
                                            columnMenu={ColumnMenuCheckboxFilter}
                                            filterable={true}
                                            filter={column?.filter}
                                            format={column?.format}
                                            editable={column?.editable}
                                            hidden={column?.hidden}
                                          />
                                          :
                                          column.field == 'serialNumber' ?
                                            <Column
                                              field={column.field}
                                              title={localizationService.toLanguageString('synoptic_devices.serialNumber', 'NOT_FOUND')}
                                              filterable={true}
                                              columnMenu={ColumnMenuCheckboxFilter}
                                              filter={column?.filter}
                                              format={column?.format}
                                              editable={column?.editable}
                                              hidden={column?.hidden}
                                              cells={{
                                                headerCell: IconHeaderCell
                                              }}
                                            />
                                            :
                                            column.field == 'name' ?
                                              <Column
                                                field={column.field}
                                                title={localizationService.toLanguageString('synoptic_devices.name', 'NOT_FOUND')}
                                                filterable={true}
                                                columnMenu={ColumnMenuCheckboxFilter}
                                                filter={column?.filter}
                                                format={column?.format}
                                                editable={false}
                                                hidden={column?.hidden}
                                                cells={{
                                                  data: NameEditCell,
                                                  headerCell: IconHeaderCell
                                                }}
                                              />
                                              :
                                              column.field == 'zoneNameListJoined' ?
                                                <Column
                                                  field={column.field}
                                                  title={localizationService.toLanguageString('synoptic_devices.zoneNameListJoined', 'NOT_FOUND')}
                                                  filterable={true}
                                                  columnMenu={ColumnMenuCheckboxFilter}
                                                  cells={{
                                                    headerCell: IconHeaderCell
                                                  }}
                                                  filter={column?.filter}
                                                  format={column?.format}
                                                  editable={column?.editable}
                                                  hidden={column?.hidden}
                                                />
                                                :
                                                column.field == 'deviceTypeName' ?
                                                  <Column
                                                    field={column.field}
                                                    title={localizationService.toLanguageString('synoptic_devices.deviceTypeName', 'NOT_FOUND')}
                                                    filterable={true}
                                                    columnMenu={ColumnMenuCheckboxFilter}
                                                    cells={{
                                                      headerCell: IconHeaderCell
                                                    }}
                                                    filter={column?.filter}
                                                    format={column?.format}
                                                    editable={column?.editable}
                                                    hidden={column?.hidden}
                                                  />
                                                  :
                                                  <Column
                                                    field={column?.field}
                                                    title={column?.title}
                                                    filterable={true}
                                                    columnMenu={ColumnMenuCheckboxFilter}
                                                    filter={column?.filter}
                                                    format={column?.format}
                                                    editable={column?.editable}
                                                    hidden={column?.hidden}
                                                    editor={column?.editor}
                                                    cells={{ headerCell: IconHeaderCell }}
                                                  //width={'2000'}
                                                  />
              )
            )
            :
            columns?.columns?.map((column: any, index: any) =>
              columns.show && (
                <Column
                  key={index}
                  field={column?.field}
                  title={column?.title}
                  filterable={column?.filterable}
                  columnMenu={ColumnMenuCheckboxFilter}
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                  hidden={column?.hidden}
                  editor={column?.editor}
                />
              )
            )
        }
      </Grid>

      <GridPDFExport ref={_pdfExport} margin="1cm">
        {
          columns?.length > 0 ?
            columns?.map((column: any, index: any) =>
              column.show && (
                <Column
                  key={index}
                  field={column?.field}
                  title={column?.title}
                  filterable={column?.filterable}
                  columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                  className='k-text-center'
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                  editor={column?.editor}
                />
              )
            )
            :
            columns?.columns?.map((column: any, index: any) =>
              columns.show && (
                <Column
                  key={index}
                  field={column?.field}
                  title={column?.title}
                  filterable={column?.filterable}
                  columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                  className='k-text-center'
                  filter={column?.filter}
                  format={column?.format}
                  editable={column?.editable}
                  editor={column?.editor}
                />
              )
            )
        }
        <Grid
          data={devicesOn}
          dataItemKey={"mac"}
          skip={dataState.skip}
          take={dataState.take}
          total={countDevicesOn}
          filterable={false}
          sortable={true}
          reorderable={false}
          pageable={{ type: 'input', buttonCount: 5, pageSizes: [5, 10, 50, 100], info: true }}
          sort={dataState.sort}
          filter={dataState.filter}
          //onFilterChange={onFilterChange}
          onColumnReorder={handleColumnReorder}
          onColumnResize={onColumnResize}
          onDataStateChange={onDataStateChange}
          onPageChange={onPageChange}
          onItemChange={itemChange}
          edit={edit}
          editable={true}
          scrollable={'none'}
        >
          <GridToolbar>
            <div>
              <h3>{localizationService.toLanguageString('synoptic_devices.title', 'NOT_FOUND')}</h3>
            </div>
            <div style={{ right: '15px', float: 'right', position: 'absolute', display: 'flex', gap: '8px' }}>
              <ConfiguratorToggleColumnsGrid columns={columns} onChange={onChange} />
              <div>
                <CSVLink data={json2csv(devices)} separator={","}>
                  <Button
                    title={localizationService.toLanguageString('synoptic_devices.export_to_csv', 'NOT_FOUND')}
                    themeColor={"primary"}
                    size="small"
                    svgIcon={fileCsvIcon}
                  />
                </CSVLink>
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.export_to_pdf', 'NOT_FOUND')}
                  onClick={pdfExport}
                  themeColor={"primary"}
                  size="small"
                  svgIcon={filePdfIcon}
                />
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.export_to_excel', 'NOT_FOUND')}
                  themeColor={"primary"}
                  size="small"
                  svgIcon={fileExcelIcon}
                  onClick={excelExport}
                />
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.save_config', 'NOT_FOUND')}
                  themeColor={'primary'}
                  size="small"
                  svgIcon={saveIcon}
                  onClick={saveStateToLocalStorage}
                >
                </Button>
              </div>
              <div>
                <Button
                  title={localizationService.toLanguageString('synoptic_devices.load_config', 'NOT_FOUND')}
                  themeColor={'primary'}
                  size="small"
                  svgIcon={uploadIcon}
                  onClick={loadStateFromLocalStorage}
                >
                </Button>
              </div>
            </div>
          </GridToolbar>
          <Column
            cells={{ data: (props: any) => <EditingCell  {...props} {...commandCellProps} /> }}
            editable={false}
            filterable={false}
          />
          <Column
            cells={{ data: LinkMapCell }}
            editable={false}
            filterable={false}
          />
          <Column
            cells={{ data: LinkDeviceManagement }}
            editable={false}
            filterable={false}
            sortable={false}
          />
          {
            columns?.length > 0 ?
              columns?.map((column: any, index: any) =>
                column.show && (
                  <Column
                    key={index}
                    field={column?.field}
                    title={column?.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    filter={column?.filter}
                    format={column?.format}
                    editable={column?.editable}
                    editor={column?.editor}
                  />
                )
              )
              :
              columns?.columns?.map((column: any, index: any) =>
                columns.show && (
                  <Column
                    key={index}
                    field={column?.field}
                    title={column?.title}
                    filterable={column?.filterable}
                    columnMenu={column?.columnMenu ? ColumnMenuCheckboxFilter : undefined}
                    className='k-text-center'
                    filter={column?.filter}
                    format={column?.format}
                    editable={column?.editable}
                    editor={column?.editor}
                  />
                )
              )

          }
          <Column
            field='status'
            title={localizationService.toLanguageString('synoptic_devices.status', 'NOT_FOUND')}
            cells={{ data: (props) => <StatusCellWithColorComponent  {...props} /> }}
            editable={false}
            filterable={false}
            sortable={true}
          />
          <Column
            field='groupName'
            title={localizationService.toLanguageString('synoptic_devices.groupName', 'NOT_FOUND')}
            cells={{ data: (props) => <GroupCellWithColorComponent  {...props} /> }}
            editable={false}
            filterable={false}
            columnMenu={ColumnMenuCheckboxFilter}
            sortable={true}
          />
          <Column
            field='jobName'
            title={localizationService.toLanguageString('synoptic_devices.jobName', 'NOT_FOUND')}
            cells={{ data: (props) => <JobCellWithColorComponent  {...props} /> }}
            editable={false}
            filterable={false}
            columnMenu={ColumnMenuCheckboxFilter}
            sortable={true}
          />
          <Column
            field='dateTime'
            title={localizationService.toLanguageString('synoptic_devices.dateTime', 'NOT_FOUND')}
            cells={{ data: (props) => <DateTimeCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => { }}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter={'date'}
            sortable={true}
          />
          <Column
            field='lastPositionDate'
            title={localizationService.toLanguageString('synoptic_devices.lastPositionDate', 'NOT_FOUND')}
            cells={{ data: (props) => <LastPositionDateCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => { }}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter={'date'}
            sortable={true}
          />
          <Column
            field='localLastPositionDate'
            title={localizationService.toLanguageString('synoptic_devices.localLastPositionDate', 'NOT_FOUND')}
            cells={{ data: (props) => <LocalLastPositionDateCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => { }}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter='date'
            sortable={true}
          />
          <Column
            field='localTime'
            title={localizationService.toLanguageString('synoptic_devices.localTime', 'NOT_FOUND')}
            cells={{ data: (props) => <LocalTimeCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            columnMenu={(props) => (
              <GridColumnMenuFilter
                {...props}
                alwaysExpand
                filterUI={CustomGridFilterDateTime}
                onExpandChange={() => { }}
                onCloseMenu={() => {
                  localStorage.removeItem('filterOpenedColumn');
                }}
              />
            )}
            filter='date'
            sortable={true}
          />
          <Column
            field='temperature'
            title={localizationService.toLanguageString('synoptic_devices.temperature', 'NOT_FOUND')}
            cells={{ data: (props) => <TemperatureCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            filter='numeric'
            sortable={true}
          />
          <Column
            field='battery'
            title={localizationService.toLanguageString('synoptic_devices.battery', 'NOT_FOUND')}
            cells={{ data: (props) => <BatteryCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={true}
            filter='numeric'
            sortable={true}
          />
          <Column
            field='inCharge'
            title={localizationService.toLanguageString('synoptic_devices.inCharge', 'NOT_FOUND')}
            cells={{ data: (props) => <InChargeCellWithColorComponent  {...props} /> }}
            editable={false}
            filterable={false}
            sortable={true}
          />
          <Column
            field='alarm'
            title={localizationService.toLanguageString('synoptic_devices.hasAlarm', 'NOT_FOUND')}
            cells={{ data: (props) => <AlarmCellWithBackGroundComponent  {...props} /> }}
            editable={false}
            filterable={false}
            sortable={false}
          />
        </Grid>
      </GridPDFExport>
    </div>
  );
};